"use client";

import { useEffect } from "react";
import {
  getLandingSectionIdFromHref,
  scrollToLandingSection,
} from "@/lib/landing-section-navigation";
import {
  DEFAULT_LANDING_VARIANT,
  type LandingVariant,
} from "./landing-preview";
import LandingPageMeridian from "./layouts/meridian";

interface LandingPageClientProps {
  previewMode?: boolean;
  lockPreviewNavigation?: boolean;
  variant?: LandingVariant;
}

export default function LandingPageClient({
  previewMode = false,
  lockPreviewNavigation = false,
  variant = DEFAULT_LANDING_VARIANT,
}: LandingPageClientProps) {
  const activeVariant = variant;

  useEffect(() => {
    if (!previewMode || !lockPreviewNavigation) return;

    const handlePreviewAnchorClick = (event: MouseEvent) => {
      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      const anchor = target.closest("a[href]");
      const href = anchor?.getAttribute("href");

      if (!anchor || !href) {
        return;
      }

      if (!href.startsWith("/") && !href.startsWith("#")) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const normalizedHref = href.startsWith("#") ? `/${href}` : href;
      const sectionId = getLandingSectionIdFromHref(normalizedHref);
      const behavior = window.matchMedia("(prefers-reduced-motion: reduce)")
        .matches
        ? "auto"
        : "smooth";

      if (sectionId) {
        scrollToLandingSection(sectionId, behavior);
        return;
      }

      window.scrollTo({ top: 0, behavior });
    };

    document.addEventListener("click", handlePreviewAnchorClick, true);

    return () => {
      document.removeEventListener("click", handlePreviewAnchorClick, true);
    };
  }, [lockPreviewNavigation, previewMode]);

  return <LandingPageMeridian key={activeVariant} />;
}
